import { Ref, useMemo } from 'react';
import type { SpeechPosition } from '../../../lib/speech-script';
import { messageDir } from '../../../lib/message-direction';
import styles from './BriefListenModal.module.css';

/**
 * The script, with the voice's own highlighter trail drawn across it.
 *
 * **The edge has to move like a pen, not like a cursor.** A character is roughly 55ms of speech, so a
 * marker that can only sit between characters visibly steps. Three things together make it sweep:
 *
 * 1. the position is sampled every frame (see `useBriefAudio`),
 * 2. it carries a FRACTION of a character (`charactersReadExact`), and
 * 3. the character that fraction lands inside is painted with a gradient stop at exactly that fraction —
 *    so the edge can sit anywhere, including halfway through a letter.
 *
 * Everything behind the edge stays marked, so the boundary between marked and unmarked text IS the
 * position and a listener can see how far in they are at a glance.
 *
 * The text either side of the live sentence is memoized on the sentence index: at 60 frames a second, the
 * only thing that should be rebuilt is the sentence being spoken.
 */
export function BriefScriptText({
  segments,
  text,
  position,
  edgeRef,
}: {
  segments: string[];
  text: string;
  position: SpeechPosition;
  /** Attached to the marker's own edge, so the caller can keep it in view. */
  edgeRef?: Ref<HTMLSpanElement>;
}) {
  // The reading direction is decided from the WHOLE script — this is assistant-authored text, and the
  // majority-script rule is meaningless on a fragment (an English brand name opening a Hebrew sentence
  // would flip that sentence alone).
  const dir = messageDir(text);
  const index = position.index;

  const read = useMemo(() => (index > 0 ? `${segments.slice(0, index).join(' ')} ` : ''), [segments, index]);
  const unread = useMemo(
    () => (index >= 0 && index < segments.length - 1 ? ` ${segments.slice(index + 1).join(' ')}` : ''),
    [segments, index],
  );

  if (index < 0) {
    // Nothing has been spoken yet: the whole script, unmarked. It is all present from the first frame —
    // the point is seeing what is coming, not having it revealed.
    return (
      <p className={styles.script} dir={dir}>
        {segments.join(' ')}
      </p>
    );
  }

  const segment = segments[index] ?? '';
  const exact = Math.min(Math.max(position.charactersReadExact, 0), segment.length);
  const whole = Math.floor(exact);
  const fraction = exact - whole;

  return (
    <p className={styles.script} dir={dir}>
      {read && <span className={styles.read}>{read}</span>}
      <span className={styles.read}>{segment.slice(0, whole)}</span>
      {/* The one character the edge is inside. `--fill` is where the ink stops, so the sweep is
          continuous rather than one character at a time; the gradient runs along the inline axis, so RTL
          fills from the right without a second rule. */}
      {whole < segment.length && (
        <span ref={edgeRef} className={styles.edge} style={{ '--fill': `${fraction * 100}%` } as React.CSSProperties}>
          {segment[whole]}
        </span>
      )}
      {segment.slice(whole + 1)}
      {unread}
    </p>
  );
}
